<template>
  <component
    :is="inputComponent"
    v-bind="inputProps"
    data-pendo-gc="form_input"
    :error="meta.touched && errors.length > 0"
    :name="name"
    :value="inputValue"
    v-on="componentEventHandlers"
    @blur="handleBlur"
    @update:value="handleAndEmitChange"
  >
    <template #errors>
      <InputErrors
        v-if="errors && meta.touched"
        :errors="errors"
      />
    </template>
    <template #card-extra="{ label, value }">
      <slot
        :label="label"
        name="custom-content"
        :value="value"
      />
    </template>
  </component>
</template>

<script>
import { toRef } from 'vue'
import { useField } from 'vee-validate'
import TextInput from './TextInput.vue'
import SelectInput from './SelectInput.vue'
import RadioInput from './RadioInput.vue'
import TimeInput from './TimeInput.vue'
import TextAreaInput from './TextAreaInput.vue'
import NumberInput from './NumberInput.vue'
import InputErrors from './InputErrors.vue'
import EverydayToggle from './EverydayToggle.vue'
import SegmentedControl from './SegmentedControl.vue'
import CardInput from './CardInput.vue'
import HiddenInput from './HiddenInput.vue'
import NumberInputStepper from './NumberInputStepper.vue'

const FORM_TYPES = ['text', 'select', 'radio', 'textarea', 'time', 'toggle', 'number', 'card', 'hidden', 'number-stepper']

export default {
  name: 'FormInput',
  components: {
    InputErrors,
  },
  props: {
    name: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      default: 'text',
      validator: (value) => FORM_TYPES.includes(value),
    },
    inputProps: {
      type: Object,
      default: () => ({}),
    },
    initialValue: {
      type: [String, Boolean, Number, Array],
      default: '',
    },
    componentEventHandlers: {
      type: Object,
      default: () => ({}),
    },
    keepValueOnUnmount: {
      type: [Boolean, null],
      default: null,
    },
  },
  emits: ['update:value'],
  setup(props) {
    const name = toRef(props, 'name')
    const initialValue = toRef(props, 'initialValue')
    const type = toRef(props, 'type')
    const keepValueOnUnmount = toRef(props, 'keepValueOnUnmount')
    const useFieldOptions = {
      type: type.value,
      initialValue: initialValue.value,
      ...(keepValueOnUnmount.value !== null && { keepValueOnUnmount: keepValueOnUnmount.value }),
    }

    const {
      value: inputValue,
      errors,
      handleBlur,
      handleChange,
      meta,
    } = useField(name, undefined, useFieldOptions)

    return {
      inputValue,
      errors,
      handleBlur,
      handleChange,
      meta,
    }
  },
  computed: {
    inputComponent() {
      switch (this.type) {
        case 'select':
          return SelectInput
        case 'radio':
          return RadioInput
        case 'textarea':
          return TextAreaInput
        case 'time':
          return TimeInput
        case 'toggle':
          return EverydayToggle
        case 'number':
          return NumberInput
        case 'segmented-control':
          return SegmentedControl
        case 'card':
          return CardInput
        case 'number-stepper':
          return NumberInputStepper
        case 'hidden':
          return HiddenInput
        default:
          return TextInput
      }
    },
  },
  methods: {
    handleAndEmitChange(value) {
      this.handleChange(value)
      this.$emit('update:value', value)
    },
  },
}
</script>
